import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FaChevronLeft, FaChevronRight, FaPlay, FaArrowRight } from 'react-icons/fa'
import { motion, AnimatePresence } from 'framer-motion'
import ImageWithFallback from './ImageWithFallback'

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0)

  const slides = [
    {
      id: 1,
      image: 'https://media.ouest-france.fr/v1/pictures/MjAxNzA5NDFiNDNhZDQ2NTBjZGUwMGU4ZGFmYzg2ZjFkZWZjMTA?width=630&height=354&focuspoint=50%2C25&cropresize=1&client_id=bpeditorial&sign=67fae986cd2ec9228a3e7cc7aec93aeaba0d809125dc045af938d58bec61e6ed',
      title: 'Production Rizicole Moderne',
      subtitle: 'Casiers aménagés SAED',
      description: 'Culture intensive de riz irrigué dans les casiers aménagés de la vallée du fleuve Sénégal, suivant les modèles SAED.',
      stats: [
        { value: '320', label: 'Hectares' },
        { value: '100', label: 'Sacs/Ha' }
      ],
      highlights: [
        'Techniques SAED',
        'Irrigation moderne'
      ],
      cta: 'Découvrir nos activités',
      link: '/nos-activites'
    },
    {
      id: 2,
      image: 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
      title: 'Élevage Moderne au Sénégal',
      subtitle: 'Plus de 3% du PIB national',
      description: 'Élevage de zébus Gobra et amélioration génétique contribuant significativement à l\'économie nationale sénégalaise.',
      stats: [
        { value: '3%+', label: 'PIB national' },
        { value: '44%', label: 'Ménages impliqués' }
      ],
      highlights: [
        'Zébus Gobra',
        'Amélioration génétique'
      ],
      cta: 'Nos activités d\'élevage',
      link: '/nos-activites'
    },
    {
      id: 3,
      image: 'https://saed.sn/static/assets/images/services/service-2-6.png',
      title: 'Aménagements Hydro-Agricoles',
      subtitle: 'Infrastructure du delta',
      description: 'Développement d\'infrastructures d\'irrigation et d\'aménagement des terres dans le delta du fleuve Sénégal.',
      stats: [
        { value: '2,500', label: 'Hectares' },
        { value: '25', label: 'Projets' }
      ],
      highlights: [
        'Gestion durable de l\'eau',
        'Partenariats AFD'
      ],
      cta: 'Nos partenariats',
      link: '/partenaires'
    },
    {
      id: 4,
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
      title: 'Agro-Pastoralisme Résilient',
      subtitle: 'Adaptation climatique',
      description: 'Systèmes intégrés agriculture-élevage renforçant la résilience des communautés face aux changements climatiques.',
      stats: [
        { value: '16', label: 'GIE partenaires' },
        { value: '345', label: 'Hectares aménagés' }
      ],
      highlights: [
        'Services climatiques',
        'Résilience communautaire'
      ],
      cta: 'Voir nos actualités',
      link: '/actualites'
    }
  ]

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length)
    }, 6000)

    return () => clearInterval(timer)
  }, [slides.length])

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1))
  }

  const goToSlide = (index) => {
    setCurrentSlide(index)
  }

  return (
    <section className="relative h-screen sm:h-[80vh] lg:h-[90vh] overflow-hidden bg-gradient-to-br from-primary-900 to-accent-900">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
          className="absolute inset-0"
        >
          {/* Background Image with Overlay */}
          <div className="relative h-full">
            <ImageWithFallback
              src={slides[currentSlide].image}
              alt={slides[currentSlide].title}
              className="absolute inset-0 w-full h-full object-cover"
            />
            
            {/* Advanced Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-900/50 to-accent-900/50 mix-blend-overlay" />
            
            {/* Animated Patterns */}
            <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")', opacity: 0.1 }} />
            
            {/* Content Container - Now fully responsive */}
            <div className="absolute inset-0 flex items-center justify-center px-4 sm:px-6 lg:px-8">
              <div className="container mx-auto max-w-7xl h-full flex items-center justify-center">
                <div className="relative w-full max-w-4xl">
                  {/* Main Content Box */}
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.8 }}
                    className="w-full bg-black/30 backdrop-blur-md p-4 sm:p-6 lg:p-8 rounded-2xl border-2 border-white/10 shadow-2xl"
                  >
                    {/* Title */}
                    <motion.h1
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4 }}
                      className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-3 lg:mb-4 leading-tight"
                    >
                      {slides[currentSlide].title}
                      <div className="h-1 w-16 sm:w-20 lg:w-24 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full mt-2 lg:mt-3"></div>
                    </motion.h1>

                    {/* Description */}
                    <motion.p
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="text-sm sm:text-base lg:text-lg xl:text-xl text-gray-200 mb-4 sm:mb-6 lg:mb-8 leading-relaxed"
                    >
                      {slides[currentSlide].description}
                    </motion.p>

                    {/* Stats */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.6 }}
                      className="grid grid-cols-2 gap-3 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8"
                    >
                      {slides[currentSlide].stats.map((stat, index) => (
                        <div 
                          key={index}
                          className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 lg:p-5 border border-white/20"
                        >
                          <div className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-white mb-1">
                            {stat.value}
                          </div>
                          <div className="text-xs sm:text-sm lg:text-base text-white">
                            {stat.label}
                          </div>
                        </div>
                      ))}
                    </motion.div>

                    {/* CTA Button */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.8 }}
                      className="flex gap-4"
                    >
                      <Link
                        to={slides[currentSlide].link}
                        className="inline-flex items-center px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-full font-semibold text-sm sm:text-base lg:text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
                      >
                        {slides[currentSlide].cta}
                        <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                      </Link>
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Controls */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex items-center gap-4">
        <button
          onClick={prevSlide}
          className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white border border-white/20 transition-all duration-300 hover:scale-110"
        >
          <FaChevronLeft className="text-lg" />
        </button>
        
        {/* Slide Indicators */}
        <div className="flex gap-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-white w-6'
                  : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>

        <button
          onClick={nextSlide}
          className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white border border-white/20 transition-all duration-300 hover:scale-110"
        >
          <FaChevronRight className="text-lg" />
        </button>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-8 z-20">
        <div className="flex flex-col items-center text-white/60 hover:text-white transition-colors duration-300">
          <div className="w-px h-16 bg-gradient-to-b from-white/60 to-transparent">
            <div className="w-px h-8 bg-white animate-bounce"></div>
          </div>
          <div className="mt-2 text-sm font-medium">Défiler</div>
        </div>
      </div>
    </section>
  )
}

export default Hero 